/**
 * Iter-246d — Guardian Garage Floors Learning Center.
 *
 * Mel feedback: a certification test alone fails most talent because they
 * have never been trained on the product, installation, brand standards,
 * or lead-capture playbook. This page is the structured pre-read that
 * mirrors the 22-question assessment so talent who genuinely review the
 * material can pass on their first try.
 *
 * Five modules, all expandable:
 *   1. About Guardian Garage Floors
 *   2. Product Knowledge (polyaspartic vs epoxy)
 *   3. Event & Lead Generation Training
 *   4. Booth & Event Expectations
 *   5. Frequently Asked Questions (talking points + objection handling)
 *
 * Each module renders a numbered checklist of bullets that the talent
 * can tick locally (localStorage) — purely a UX nudge, not a gate.
 * The "Take the GGF Assessment" CTA at the bottom is the natural next
 * step. The same CTA appears at the top so already-read talent can
 * jump straight in.
 *
 * Content was sourced from the official Guardian Garage Floors training
 * answers already encoded in `routes/ggf_routes.py` (pricing rules,
 * pricing-policy must-pass questions, product knowledge specifics).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  ArrowLeft, ShieldCheck, BookOpen, ChevronDown, ChevronRight, Printer,
  Briefcase, ClipboardCheck, MessageSquare, HelpCircle, Award, Info,
} from 'lucide-react';

// Module content. Each `bullets` item is rendered as a checkable row.
// Authentic GGF content — no placeholders. Aligned with the 22-question
// official assessment so talent can pre-validate every answer.
const MODULES = [
  {
    id: 'about',
    icon: ShieldCheck,
    tone: 'emerald',
    title: 'About Guardian Garage Floors',
    summary: 'Company overview, brand values, and the systems we sell.',
    bullets: [
      'Guardian Garage Floors (GGF) installs premium polyaspartic floor coating systems for residential and light-commercial garages.',
      'Brand promise: a clean, durable, slip-resistant garage floor finished in a single day.',
      'Brand values: professionalism, transparency on pricing, and white-glove customer service.',
      'GGF coatings outperform traditional epoxy on UV stability, chemical resistance, and adhesion.',
      'Typical service offerings: full polyaspartic floor coating, chip-blend finishes, decorative flake systems, and concrete repair when needed.',
      'Your role at events: represent the brand, qualify homeowners, capture leads. You do NOT sell or quote on-site.',
    ],
  },
  {
    id: 'product',
    icon: BookOpen,
    tone: 'cyan',
    title: 'Product Knowledge',
    summary: 'Polyaspartic vs epoxy, cure times, and the GGF warranty story.',
    bullets: [
      'GGF uses polyaspartic coatings — not standard epoxy. Polyaspartic is a more advanced, faster-curing chemistry.',
      'You can WALK on a freshly coated GGF floor within 4–6 hours of install.',
      'You can DRIVE on a freshly coated GGF floor after 24 hours.',
      'GGF coatings are approximately 4× stronger than traditional epoxy.',
      'Polyaspartic resists UV fading, hot tires, motor oil, road salt, and household chemicals.',
      'GGF systems are backed by a lifetime warranty on the coating bond when installed by a GGF technician.',
      'Decorative chip / flake blends add slip resistance AND hide imperfections in the slab beneath.',
      'Installation always finishes in one day — even on a two-car garage.',
    ],
  },
  {
    id: 'leads',
    icon: ClipboardCheck,
    tone: 'amber',
    title: 'Event & Lead Generation Training',
    summary: 'How to engage homeowners, qualify, and capture leads correctly.',
    bullets: [
      'Open with a friendly, low-pressure question — e.g. "How is the floor in your garage holding up?" or "Are you using your garage for storage, a workshop, or just parking?".',
      'Qualify by asking about: garage size (1, 2, or 3 car), current floor condition, timeline ("are you thinking weeks, months, or just researching?"), and homeownership.',
      'A VALID lead requires: First name, Last name, Phone number, Email, ZIP code, and Garage size. Missing ANY field = the lead does not count.',
      'All leads MUST be entered into the official GGF lead capture system (the tablet / form provided at the booth). No paper notes, no personal phone notes.',
      'Swag is given AFTER a valid lead is captured — never before. This avoids "swag hunters" who never intend to engage.',
      'Correct lead flow: Greet → Qualify → Capture in system → Confirm contact details → Hand off swag → Thank.',
      'If a homeowner is "just looking", still capture the lead with permission. Many buyers return weeks later.',
    ],
  },
  {
    id: 'booth',
    icon: Briefcase,
    tone: 'violet',
    title: 'Booth & Event Expectations',
    summary: 'Dress code, arrival, setup, breakdown, and professional conduct.',
    bullets: [
      'Dress code: clean, branded GGF shirt (provided) with neat dark pants or khakis. Closed-toe shoes only. No hats unless GGF-branded.',
      'Arrival: be on-site 30 minutes before the event opens to the public. Late arrivals risk being sent home unpaid.',
      'Setup: assist with banner display, tablet placement, and swag organization. Do NOT touch product samples without permission.',
      'During the event: NEVER sit unless on an official break. Stand, smile, engage every guest who makes eye contact.',
      'No phones at the booth except to capture leads in the GGF system. Personal phone use is breakroom only.',
      'Breakdown: stay until released. Help load banners and tablets back into branded crates. Walk-offs forfeit the recap and rating.',
      'Professional conduct: no profanity, no negative talk about competitors, no political or religious topics with guests.',
    ],
  },
  {
    id: 'faq',
    icon: MessageSquare,
    tone: 'rose',
    title: 'Frequently Asked Questions (Approved Talking Points)',
    summary: 'Customer questions, approved responses, and objection handling.',
    bullets: [
      '"How much does it cost?" → "Pricing depends on garage size and floor condition. A GGF specialist will follow up with a free in-home quote — can I capture your contact info?". You are NEVER allowed to give a price, range, or estimate.',
      '"Can you give me a ballpark?" → Same answer. Even a range counts as quoting and violates GGF policy.',
      '"How long does it take?" → "One day. Walk on it in 4–6 hours, drive on it after 24 hours."',
      '"How is this different from Home Depot epoxy kits?" → "Different chemistry — polyaspartic, not epoxy. About 4× stronger, UV-stable, and lifetime warranted when installed by a GGF tech."',
      '"Is it slippery when wet?" → "The decorative chip finish adds slip resistance. Many customers prefer it specifically because it stays grippy when wet."',
      '"How long does it last?" → "Lifetime warranty on the bond. The decorative layer holds up for decades with normal use."',
      'Objection: "I need to talk to my spouse." → "Totally understood. Let me get your details so a GGF specialist can send you both the same information by email."',
      'Objection: "I am just looking today." → "Perfect — most of our best customers start as researchers. Mind if I get your email so we can send you the product overview?"',
    ],
  },
];

const toneClass = {
  emerald: { ring: 'border-emerald-300', dot: 'bg-emerald-500', iconBg: 'bg-emerald-100 text-emerald-700' },
  cyan:    { ring: 'border-cyan-300',    dot: 'bg-cyan-500',    iconBg: 'bg-cyan-100 text-cyan-700' },
  amber:   { ring: 'border-amber-300',   dot: 'bg-amber-500',   iconBg: 'bg-amber-100 text-amber-700' },
  violet:  { ring: 'border-violet-300',  dot: 'bg-violet-500',  iconBg: 'bg-violet-100 text-violet-700' },
  rose:    { ring: 'border-rose-300',    dot: 'bg-rose-500',    iconBg: 'bg-rose-100 text-rose-700' },
};

const STORAGE_KEY = 'ievntur_ggf_learning_v1';

const Module = ({ mod, open, onToggle, ticked, onTick }) => {
  const t = toneClass[mod.tone] || toneClass.emerald;
  const Icon = mod.icon;
  const allTicked = mod.bullets.every((_, i) => ticked[`${mod.id}-${i}`]);
  return (
    <Card className={`border-2 ${allTicked ? t.ring : 'border-slate-200'}`} data-testid={`ggf-module-${mod.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-4 flex items-center gap-3 hover:bg-slate-50 transition"
        data-testid={`ggf-module-toggle-${mod.id}`}
      >
        <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${t.iconBg}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-slate-900">{mod.title}</div>
          <div className="text-xs text-slate-500 truncate">{mod.summary}</div>
        </div>
        {allTicked && (
          <span className={`text-[10px] font-bold uppercase tracking-wider text-white ${t.dot} rounded-full px-2 py-0.5`}>
            Reviewed
          </span>
        )}
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <CardContent className="pt-0 pb-4 space-y-2">
          {mod.bullets.map((line, i) => {
            const k = `${mod.id}-${i}`;
            return (
              <label
                key={k}
                className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer hover:bg-slate-50 rounded p-1.5"
                data-testid={`ggf-bullet-${k}`}
              >
                <input
                  type="checkbox"
                  checked={!!ticked[k]}
                  onChange={(e) => onTick(k, e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded text-cyan-600 focus:ring-cyan-500 shrink-0"
                />
                <span className={ticked[k] ? 'text-slate-500 line-through' : ''}>{line}</span>
              </label>
            );
          })}
        </CardContent>
      )}
    </Card>
  );
};

const GgfLearningCenter = () => {
  const navigate = useNavigate();
  const [openIds, setOpenIds] = useState({ [MODULES[0].id]: true });
  const [ticked, setTicked] = useState({});

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setTicked(JSON.parse(raw));
    } catch (_e) { /* noop */ }
  }, []);

  const persist = (next) => {
    setTicked(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (_e) { /* noop */ }
  };

  const totalBullets = MODULES.reduce((n, m) => n + m.bullets.length, 0);
  const tickedCount = MODULES.reduce(
    (n, m) => n + m.bullets.filter((_, i) => ticked[`${m.id}-${i}`]).length,
    0,
  );
  const pct = Math.round((tickedCount / totalBullets) * 100);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-5 print:p-2">
        <div className="flex items-center gap-3 print:hidden">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="ggf-learning-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-emerald-600" />
            GGF Learning Center
          </h1>
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={() => window.print()}
            data-testid="ggf-learning-print-btn"
          >
            <Printer className="w-3.5 h-3.5 mr-1" /> Print / save as PDF
          </Button>
        </div>

        <Alert className="border-emerald-200 bg-emerald-50 text-emerald-900">
          <Info className="w-4 h-4" />
          <AlertDescription>
            <div className="text-sm">
              Work through every module below before you take the assessment. Most failures
              happen because talent skip Module 2 (Product Knowledge) and Module 3 (Lead
              Process). Pass mark is <strong>80%</strong> and the three pricing-policy questions
              are <strong>auto-fail</strong> if you get them wrong.
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs">
              <div className="flex-1 h-2 bg-emerald-100 rounded-full overflow-hidden" data-testid="ggf-learning-progress">
                <div
                  className="h-full bg-emerald-500 transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="font-semibold whitespace-nowrap" data-testid="ggf-learning-progress-pct">
                {tickedCount} / {totalBullets} reviewed
              </span>
            </div>
          </AlertDescription>
        </Alert>

        <div className="space-y-3">
          {MODULES.map((m) => (
            <Module
              key={m.id}
              mod={m}
              open={!!openIds[m.id]}
              onToggle={() => setOpenIds((s) => ({ ...s, [m.id]: !s[m.id] }))}
              ticked={ticked}
              onTick={(k, v) => persist({ ...ticked, [k]: v })}
            />
          ))}
        </div>

        <Card className="border-emerald-300 bg-emerald-50/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-emerald-900">
              <Award className="w-5 h-5 text-emerald-700" />
              Ready for the assessment?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-emerald-900/80">
              You can take the GGF assessment at any time, but talent who complete every module
              first pass on their first attempt {pct >= 80 ? '— and you are ' + pct + '% through. Nice work.' : '— you are currently ' + pct + '% through.'}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => navigate('/talent/certifications/ggf')}
                data-testid="ggf-learning-take-assessment-btn"
              >
                <Award className="w-4 h-4 mr-2" />
                Take GGF Assessment
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setOpenIds(MODULES.reduce((s, m) => ({ ...s, [m.id]: true }), {}));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                data-testid="ggf-learning-expand-all-btn"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                Expand all modules
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GgfLearningCenter;
